import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import dayjs from 'dayjs';
import { KpiDetailsDialogComponent } from 'src/app/components/main/employees/performance/my-metrics/kpi-details-dialog/kpi-details-dialog.component';
import {
  ScheduleAndRemoteWorkComplianceCalendarCell,
  ScheduleAndRemoteWorkComplianceCalendarDialogData,
  ScheduleAndRemoteWorkComplianceDayCustom,
} from 'src/app/models/schedule-and-remote-work-compliance-kpi-data.model';

@Component({
  selector: 'app-schedule-and-remote-work-compliance-calendar-dialog',
  imports: [CommonModule, DecimalPipe, MatTooltipModule, KpiDetailsDialogComponent],
  templateUrl: './schedule-and-remote-work-compliance-calendar-dialog.component.html',
  styleUrl: './schedule-and-remote-work-compliance-calendar-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleAndRemoteWorkComplianceCalendarDialogComponent {
  private readonly date_pipe = inject(DatePipe);

  readonly data = inject<ScheduleAndRemoteWorkComplianceCalendarDialogData>(MAT_DIALOG_DATA);

  readonly weekday_labels = ['L', 'M', 'X', 'J', 'V', 'S', 'D'] as const;

  readonly title = horario_teletrabajo_label(this.format_month_label(this.data.month_end_date));

  readonly subtitle = this.data.tooltip_summary;

  readonly selected_date = linkedSignal(() => this.default_selected_date());

  readonly selected_day = computed((): ScheduleAndRemoteWorkComplianceDayCustom | null => {
    const selected_date = this.selected_date();

    if (selected_date === null) {
      return null;
    }

    return this.data.days.find(item => item.date === selected_date) ?? null;
  });

  readonly calendar_cells = build_calendar_cells(this.data.month_start_date, this.data.days);

  readonly calendar_aria_label = horario_teletrabajo_label(this.format_month_label(this.data.month_end_date));

  private readonly default_selected_date = computed((): string | null => {
    const days = this.data.days;
    const today = dayjs().format('YYYY-MM-DD');

    if (days.some(day => day.date === today && !day.is_day_off)) {
      return today;
    }

    return days.find(day => !day.is_day_off && !day.is_day_ok)?.date ?? null;
  });

  selectDay(date: string): void {
    this.selected_date.set(date);
  }

  private format_month_label(month_end_date: string): string {
    return this.date_pipe.transform(month_end_date, 'LLLL', '', 'es-ES') ?? month_end_date;
  }
}

function horario_teletrabajo_label(month_name: string): string {
  return `Horario y teletrabajo de ${month_name}`;
}

function build_calendar_cells(
  month_start_date: string,
  days: ScheduleAndRemoteWorkComplianceDayCustom[]
): ScheduleAndRemoteWorkComplianceCalendarCell[] {
  const start_of_month = dayjs(month_start_date).tz('Europe/Madrid').startOf('month');

  if (!start_of_month.isValid()) {
    return [];
  }

  const days_in_month = start_of_month.daysInMonth();
  const leading_empty_count = start_of_month.isoWeekday() - 1;
  const days_by_date = new Map(days.map(day => [day.date, day]));
  const cells: ScheduleAndRemoteWorkComplianceCalendarCell[] = [];
  const today = dayjs().format('YYYY-MM-DD');

  for (let index = 0; index < leading_empty_count; index++) {
    cells.push(empty_cell(`leading-${index}`));
  }

  for (let day_number = 1; day_number <= days_in_month; day_number++) {
    const date = start_of_month.date(day_number).format('YYYY-MM-DD');
    const day = days_by_date.get(date) ?? null;

    cells.push({
      id: date,
      date,
      day_number,
      evaluation: day,
      is_day_off: day?.is_day_off ?? false,
      is_vacation: day?.is_vacation ?? false,
      is_holiday: day?.is_holiday ?? false,
      is_absence: day?.is_absence ?? false,
      schedule_mark: day?.schedule_mark ?? '',
      remote_mark: day?.remote_mark ?? '',
      day_off_emoji: day?.day_off_emoji ?? null,
      aria_label: day === null ? `Día ${day_number}, no calculable` : day_aria_label(day, day_number),
      is_today: date === today,
    });
  }

  const trailing_empty_count = (7 - (cells.length % 7)) % 7;

  for (let index = 0; index < trailing_empty_count; index++) {
    cells.push(empty_cell(`trailing-${index}`));
  }

  return cells;
}

function empty_cell(id: string): ScheduleAndRemoteWorkComplianceCalendarCell {
  return {
    id,
    date: null,
    day_number: null,
    evaluation: null,
    is_day_off: false,
    is_vacation: false,
    is_holiday: false,
    is_absence: false,
    schedule_mark: '',
    remote_mark: '',
    day_off_emoji: null,
    aria_label: '',
    is_today: false,
  };
}

function day_aria_label(day: ScheduleAndRemoteWorkComplianceDayCustom, day_number: number): string {
  if (day.is_day_off) {
    return `Día ${day_number}, ${day.day_off_label}`;
  }

  const day_status = day.is_day_ok ? 'correcto' : 'a mejorar';
  const schedule_status = day.is_schedule_ok ? 'horario correcto' : 'horario incorrecto';
  const remote_status = day.is_remote_ok ? 'teletrabajo correcto' : 'teletrabajo incorrecto';

  return `Día ${day_number}, ${day_status}, ${schedule_status}, ${remote_status}`;
}
